/**
 * tdesign v1.0.3
 * (c) 2023 tdesign
 * @license MIT
 */

import { _ as _defineProperty } from '../_chunks/dep-46de7b2e.js';
import { _ as _slicedToArray } from '../_chunks/dep-e43cf21c.js';
import { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import useConfig from '../hooks/useConfig.js';
import useControlled from '../hooks/useControlled.js';
import useCommonClassName from '../_util/useCommonClassName.js';
import { canReduceNumber, canAddNumber, formatToNumber, getMaxOrMinValidateResult, getStepValue, canInputNumber } from '../_common/js/input-number/number.js';
import '../_chunks/dep-2acfafca.js';
import '../config-provider/ConfigContext.js';
import '../_chunks/dep-d71ee5f3.js';
import '../_chunks/dep-c3f1141e.js';
import '../_chunks/dep-4b8f240e.js';
import '../_chunks/dep-9acbc163.js';
import '../_chunks/dep-7b52f080.js';
import '../_chunks/dep-f10515e8.js';
import '../_chunks/dep-4941ebe7.js';
import '../_chunks/dep-e9b62d1f.js';
import '../_chunks/dep-88cd50bf.js';
import '../_chunks/dep-dcb5aa2a.js';
import '../_chunks/dep-ee4bf8c2.js';
import '../_chunks/dep-934eda0c.js';
import '../_chunks/dep-7dce2c26.js';
import '../_chunks/dep-12510d64.js';
import '../_chunks/dep-6b568d83.js';
import '../_chunks/dep-304e657b.js';
import '../_chunks/dep-77e34309.js';
import '../_chunks/dep-6506fbc2.js';
import '../_chunks/dep-bb5d7b1e.js';
import '../_chunks/dep-e285f2f8.js';
import '../_chunks/dep-f4a320cd.js';
import '../_chunks/dep-ec70b95c.js';
import '../locale/zh_CN.js';
import '../_common/js/global-config/locale/zh_CN.js';
import 'dayjs';
import '../_chunks/dep-cbcc9f89.js';
import '../_common/js/global-config/default-config.js';
import '../_chunks/dep-f721d103.js';
import '../_chunks/dep-4559db64.js';
import '../_chunks/dep-c00532d4.js';
import '../_chunks/dep-33ffecad.js';
import '../_chunks/dep-c1f93350.js';
import '../_util/noop.js';
import '../_chunks/dep-036ad2cc.js';
import '../_chunks/dep-a827ffec.js';
import '../_chunks/dep-6c5254c6.js';
import '../_chunks/dep-0e15088f.js';
import '../_common/js/input-number/large-number.js';
import '../_common/js/log/log.js';
import '../_common/js/log/index.js';

var specialCode = ["-", ".", "e", "E"];
function useInputNumber(props) {
  var _classNames;
  var _useCommonClassName = useCommonClassName(),
    SIZE = _useCommonClassName.SIZE,
    STATUS = _useCommonClassName.STATUS;
  var _useConfig = useConfig(),
    classPrefix = _useConfig.classPrefix;
  var _useControlled = useControlled(props, "value", props.onChange),
    _useControlled2 = _slicedToArray(_useControlled, 2),
    tValue = _useControlled2[0],
    onChange = _useControlled2[1];
  var _useState = useState(""),
    _useState2 = _slicedToArray(_useState, 2),
    userInput = _useState2[0],
    setUserInput = _useState2[1];
  var _useState3 = useState(""),
    _useState4 = _slicedToArray(_useState3, 2),
    displayValue = _useState4[0],
    setDisplayValue = _useState4[1];
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    isError = _useState6[0],
    setIsError = _useState6[1];
  var inputRef = useRef(null);
  var max = props.max,
    min = props.min,
    largeNumber = props.largeNumber,
    decimalPlaces = props.decimalPlaces,
    allowInputOverLimit = props.allowInputOverLimit,
    onValidate = props.onValidate;
  var disabledReduce = props.disabled || !canReduceNumber(tValue, props.min, props.largeNumber);
  var disabledAdd = props.disabled || !canAddNumber(tValue, props.max, props.largeNumber);
  var wrapClasses = classNames("".concat(classPrefix, "-input-number"), SIZE[props.size], (_classNames = {}, _defineProperty(_classNames, STATUS.disabled, props.disabled), _defineProperty(_classNames, "".concat(classPrefix, "-is-controls-right"), props.theme === "column"), _defineProperty(_classNames, "".concat(classPrefix, "-input-number--").concat(props.theme), props.theme), _defineProperty(_classNames, "".concat(classPrefix, "-input-number--auto-width"), props.autoWidth), _classNames));
  var reduceClasses = classNames("".concat(classPrefix, "-input-number__decrease"), _defineProperty({}, STATUS.disabled, disabledReduce));
  var addClasses = classNames("".concat(classPrefix, "-input-number__increase"), _defineProperty({}, STATUS.disabled, disabledAdd));
  var getUserInput = function getUserInput(value) {
    var _inputRef$current$cur, _inputRef$current$cur2;
    if (!value && value !== 0) return "";
    var inputStr = value || value === 0 ? String(value) : "";
    if (!((_inputRef$current$cur = (_inputRef$current$cur2 = inputRef.current.currentElement).contains) !== null && _inputRef$current$cur !== void 0 && _inputRef$current$cur.call(_inputRef$current$cur2, document.activeElement))) {
      var num = formatToNumber(inputStr, {
        decimalPlaces: decimalPlaces,
        largeNumber: largeNumber
      });
      inputStr = num || num === 0 ? String(num) : "";
      if (props.format) {
        inputStr = String(props.format(value, {
          fixedNumber: inputStr
        }));
      }
    }
    return inputStr;
  };
  useEffect(function () {
    var inputValue = [void 0, null].includes(tValue) ? "" : String(tValue);
    setUserInput(getUserInput(inputValue));
  }, [tValue]);
  useEffect(function () {
    if ([void 0, "", null].includes(tValue)) return;
    var error = getMaxOrMinValidateResult({
      value: tValue,
      max: max,
      min: min,
      largeNumber: largeNumber
    });
    setIsError(error);
    onValidate === null || onValidate === void 0 ? void 0 : onValidate({
      error: error
    });
  }, [tValue, max, min, largeNumber, onValidate]);
  var handleStepValue = function handleStepValue(op) {
    var newValue = getStepValue({
      op: op,
      step: props.step,
      max: props.max,
      min: props.min,
      lastValue: tValue,
      largeNumber: props.largeNumber
    });
    var largeNumber2 = props.largeNumber,
      max2 = props.max,
      min2 = props.min;
    var overLimit = getMaxOrMinValidateResult({
      value: newValue,
      largeNumber: largeNumber2,
      max: max2,
      min: min2
    });
    return {
      overLimit: overLimit,
      newValue: newValue
    };
  };
  var handleReduce = function handleReduce(e) {
    if (disabledReduce || props.readonly) return;
    var r = handleStepValue("reduce");
    if (r.overLimit && !allowInputOverLimit) return;
    onChange(r.newValue, {
      type: "reduce",
      e: e
    });
  };
  var handleAdd = function handleAdd(e) {
    if (disabledAdd || props.readonly) return;
    var r = handleStepValue("add");
    if (r.overLimit && !allowInputOverLimit) return;
    onChange(r.newValue, {
      type: "add",
      e: e
    });
  };
  var onInnerInputChange = function onInnerInputChange(val, _ref) {
    var e = _ref.e;
    if (!canInputNumber(val, largeNumber)) return;
    if (props.largeNumber) {
      onChange(val, {
        type: "input",
        e: e
      });
      return;
    }
    var isDelete = e.nativeEvent.inputType === "deleteContentBackward";
    var inputSpecialCode = specialCode.includes(val.slice(-1)) || /\.0+$/.test(val);
    var deleteSpecialCode = isDelete && specialCode.includes(String(userInput).slice(-1));
    if (!isNaN(Number(val)) && !inputSpecialCode || deleteSpecialCode) {
      var newVal = val === "" ? void 0 : Number(val);
      onChange(newVal, {
        type: "input",
        e: e
      });
    }
    if (inputSpecialCode || deleteSpecialCode) {
      setUserInput(val);
    }
  };
  var handleBlur = function handleBlur(value, ctx) {
    var _props$onBlur;
    if (!props.allowInputOverLimit && value) {
      var r = getMaxOrMinValidateResult({
        value: tValue,
        largeNumber: largeNumber,
        max: max,
        min: min
      });
      if (r === "below-minimum") {
        onChange(min, {
          type: "blur",
          e: ctx.e
        });
        return;
      }
      if (r === "exceed-maximum") {
        onChange(max, {
          type: "blur",
          e: ctx.e
        });
        return;
      }
    }
    setUserInput(getUserInput(value));
    var newValue = formatToNumber(value, {
      decimalPlaces: decimalPlaces,
      largeNumber: largeNumber
    });
    if (newValue !== value && String(newValue) !== value) {
      onChange(newValue, {
        type: "blur",
        e: ctx.e
      });
    }
    (_props$onBlur = props.onBlur) === null || _props$onBlur === void 0 ? void 0 : _props$onBlur.call(props, newValue, ctx);
  };
  var handleFocus = function handleFocus(_, ctx) {
    var _props$onFocus;
    setUserInput(tValue);
    (_props$onFocus = props.onFocus) === null || _props$onFocus === void 0 ? void 0 : _props$onFocus.call(props, tValue, ctx);
  };
  var handleKeydown = function handleKeydown(value, ctx) {
    var _props$onKeydown;
    var e = ctx.e;
    var keyEvent = {
      ArrowUp: handleAdd,
      ArrowDown: handleReduce
    };
    if (keyEvent[e.key] !== void 0) {
      keyEvent[e.key](e);
    }
    (_props$onKeydown = props.onKeydown) === null || _props$onKeydown === void 0 ? void 0 : _props$onKeydown.call(props, value, ctx);
  };
  var handleKeyup = function handleKeyup(value, ctx) {
    var _props$onKeyup;
    (_props$onKeyup = props.onKeyup) === null || _props$onKeyup === void 0 ? void 0 : _props$onKeyup.call(props, value, ctx);
  };
  var handleKeypress = function handleKeypress(value, ctx) {
    var _props$onKeypress;
    (_props$onKeypress = props.onKeypress) === null || _props$onKeypress === void 0 ? void 0 : _props$onKeypress.call(props, value, ctx);
  };
  var handleEnter = function handleEnter(value, ctx) {
    var _props$onEnter;
    setUserInput(getUserInput(value));
    var newValue = formatToNumber(value, {
      decimalPlaces: props.decimalPlaces,
      largeNumber: props.largeNumber
    });
    if (newValue !== value && String(newValue) !== value) {
      onChange(newValue, {
        type: "enter",
        e: ctx.e
      });
    }
    (_props$onEnter = props.onEnter) === null || _props$onEnter === void 0 ? void 0 : _props$onEnter.call(props, newValue, ctx);
  };
  var handleClear = function handleClear(_ref2) {
    var e = _ref2.e;
    onChange(void 0, {
      type: "clear",
      e: e
    });
    setUserInput("");
  };
  var focus = function focus() {
    inputRef.current.focus();
  };
  var blur = function blur() {
    inputRef.current.blur();
  };
  var listeners = {
    onBlur: handleBlur,
    onFocus: handleFocus,
    onKeydown: handleKeydown,
    onKeyup: handleKeyup,
    onKeypress: handleKeypress,
    onEnter: handleEnter,
    onClick: focus,
    onClear: handleClear
  };
  return {
    classPrefix: classPrefix,
    wrapClasses: wrapClasses,
    reduceClasses: reduceClasses,
    addClasses: addClasses,
    inputRef: inputRef,
    listeners: listeners,
    displayValue: displayValue,
    setDisplayValue: setDisplayValue,
    isError: isError,
    setIsError: setIsError,
    userInput: userInput,
    setUserInput: setUserInput,
    tValue: tValue,
    focus: focus,
    blur: blur,
    onChange: onChange,
    handleReduce: handleReduce,
    handleAdd: handleAdd,
    onInnerInputChange: onInnerInputChange
  };
}

export { useInputNumber as default, specialCode };
//# sourceMappingURL=useInputNumber.js.map
